import { faPencil } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useForm } from '@tanstack/react-form'
import { observer } from 'mobx-react-lite'
import { useCallback, useContext, useId, useRef, useState } from 'react'
import type { DropdownChoice } from '@companion-app/shared/Model/Common.js'
import type { ClientInstanceConfigBase, ModuleInstanceType } from '@companion-app/shared/Model/Instance.js'
import { StaticAlert } from '~/Components/Alert'
import { Button } from '~/Components/Button'
import { Collapse } from '~/Components/Collapse'
import { DropdownInputField } from '~/Components/DropdownInputField.js'
import { SimpleDropdownInputField } from '~/Components/DropdownInputFieldSimple'
import { Form, FormLabel } from '~/Components/Form.js'
import { Grid } from '~/Components/Grid'
import { Modal } from '~/Components/Modal'
import { useAllModuleProducts } from '~/Hooks/useFilteredProducts.js'
import { ModuleVersionsRefresh } from '~/Instances/ModuleVersionsRefresh.js'
import { useModuleVersionSelectOptions } from '~/Instances/useModuleVersionSelectOptions.js'
import { useComputed } from '~/Resources/util.js'
import { RootAppStoreContext } from '~/Stores/RootAppStore.js'
import type { InstanceEditPanelService } from './InstanceEditPanelService'

interface InstanceVersionChangeButtonProps<TConfig extends ClientInstanceConfigBase> {
	id: string
	service: InstanceEditPanelService<TConfig>
	currentModuleId: string
	currentVersionId: string | null

	changeModuleDangerMessage: React.ReactNode
}

export function InstanceVersionChangeButton<TConfig extends ClientInstanceConfigBase>({
	id,
	service,
	currentModuleId,
	currentVersionId,
	changeModuleDangerMessage,
}: InstanceVersionChangeButtonProps<TConfig>): React.JSX.Element {
	const { modules } = useContext(RootAppStoreContext)

	const [show, setShow] = useState(false)
	const [advancedMode, setAdvancedMode] = useState(false)
	const originalModuleIdRef = useRef(currentModuleId) // The moduleId at the time the modal was opened

	const buttonRef = useRef<HTMLButtonElement>(null)

	const [saveError, setSaveError] = useState<string | null>(null)
	const form = useForm({
		defaultValues: {
			moduleId: currentModuleId,
			versionId: currentVersionId,
		},
		onSubmit: async ({ value }) => {
			const error = await service.setModuleAndVersion(value.moduleId, value.versionId)
			if (error) {
				setSaveError(error)
			} else {
				setSaveError(null)
				setShow(false)
			}
		},
	})

	const doShow = useCallback(
		(open: boolean) => {
			if (!open) {
				setShow(false)
			} else {
				form.reset()
				originalModuleIdRef.current = currentModuleId
				setSaveError(null)
				setAdvancedMode(false)
				setShow(true)
			}
		},
		[form, currentModuleId]
	)
	const onOpenChangeComplete = useCallback(
		(open: boolean) => {
			if (!open) {
				form.reset()
				setSaveError(null)
				setAdvancedMode(false)
			}
		},
		[form]
	)

	const handleAdvancedModeChange = useCallback(
		(open: boolean) => {
			if (!open) {
				form.setFieldValue('moduleId', originalModuleIdRef.current)
				form.setFieldValue('versionId', currentVersionId)
			}
			setAdvancedMode(open)
		},
		[form, currentVersionId]
	)

	const versionFieldId = useId()
	const moduleFieldId = useId()

	return (
		<Modal.Root open={show} onOpenChange={doShow} onOpenChangeComplete={onOpenChangeComplete}>
			<Modal.Trigger id={id} color="light" size="sm" title="Change module version" aria-label="Change module version">
				<FontAwesomeIcon icon={faPencil} />
			</Modal.Trigger>

			<Modal.Portal>
				<Modal.Backdrop />
				<Modal.Viewport>
					<Modal.Popup initialFocus={buttonRef}>
						<Modal.Header closeButton>
							<Modal.Title>Change Module Version</Modal.Title>
						</Modal.Header>
						<Form
							onSubmit={(e) => {
								e.preventDefault()
								e.stopPropagation()
								form.handleSubmit().catch((err) => {
									console.error('Error submitting form', err)
								})
							}}
						>
							<Modal.Body>
								<Grid.Row className="g-sm-2">
									<Grid.Col sm={12}>
										<StaticAlert color="warning" className="mb-3">
											Be careful when downgrading the module version. Some features may not be available in older
											versions.
										</StaticAlert>
										{!!saveError && (
											<StaticAlert color="danger" className="mb-3">
												Save failed: {saveError}
											</StaticAlert>
										)}
									</Grid.Col>

									<form.Subscribe
										selector={(state) => [state.values.moduleId, advancedMode] as const}
										children={([selectedModuleId, isAdvanced]) => {
											// In advanced mode, use the selected module from the form. In simple mode, lock to the original module.
											const effectiveModuleId = isAdvanced ? selectedModuleId : originalModuleIdRef.current

											return (
												<form.Field
													name="versionId"
													children={(field) => (
														<>
															<div className="col-sm-3 d-flex align-items-center">
																<FormLabel
																	htmlFor={versionFieldId}
																	className="col-form-label col-form-label-sm mb-0 flex-grow-1"
																>
																	Version
																</FormLabel>
																{!!modules.getStoreInfo(service.moduleType, effectiveModuleId) && (
																	<ModuleVersionsRefresh moduleType={service.moduleType} moduleId={effectiveModuleId} />
																)}
															</div>
															<Grid.Col sm={9}>
																<SelectedVersionDropdown
																	moduleType={service.moduleType}
																	moduleId={effectiveModuleId}
																	htmlName={versionFieldId}
																	value={field.state.value}
																	onChange={field.handleChange}
																	onBlur={field.handleBlur}
																/>
															</Grid.Col>
														</>
													)}
												/>
											)
										}}
									/>

									<Collapse.Root
										open={advancedMode}
										onOpenChange={handleAdvancedModeChange}
										className="col-sm-12 mt-3 mb-2 p-0"
									>
										<hr className="my-2" />
										<Collapse.Trigger className="button button-link button-sm p-0 text-decoration-none">
											<span className="me-1">{advancedMode ? '▼' : '▶'}</span>
											Advanced Options
										</Collapse.Trigger>

										<Collapse.Panel keepMounted className="row g-sm-2 p-0">
											<Grid.Col sm={12}>
												<StaticAlert color="danger" className="mt-2 mb-3">
													{changeModuleDangerMessage}
												</StaticAlert>
											</Grid.Col>

											<FormLabel htmlFor={moduleFieldId} className="col-sm-3 col-form-label col-form-label-sm">
												Module
											</FormLabel>
											<Grid.Col sm={9}>
												<form.Field
													name="moduleId"
													children={(field) => (
														<SelectedModuleDropdown
															moduleType={service.moduleType}
															htmlName={moduleFieldId}
															value={field.state.value}
															onChange={(val) => {
																field.handleChange(val)
																form.setFieldValue('versionId', null)
															}}
															onBlur={field.handleBlur}
														/>
													)}
												/>
											</Grid.Col>
										</Collapse.Panel>
									</Collapse.Root>
								</Grid.Row>
							</Modal.Body>
							<Modal.Footer>
								<form.Subscribe
									selector={(state) => [state.canSubmit, state.isSubmitting]}
									children={([canSubmit, isSubmitting]) => (
										<>
											<Modal.Close disabled={isSubmitting}>Cancel</Modal.Close>
											<Button ref={buttonRef} color="primary" type="submit" disabled={!canSubmit}>
												Save {isSubmitting ? '...' : ''}
											</Button>
										</>
									)}
								/>
							</Modal.Footer>
						</Form>
					</Modal.Popup>
				</Modal.Viewport>
			</Modal.Portal>
		</Modal.Root>
	)
}

interface SelectedModuleDropdownProps {
	moduleType: ModuleInstanceType
	htmlName: string
	value: string
	onChange: (value: string) => void
	onBlur: () => void
}

const SelectedModuleDropdown = observer(function SelectedModuleDropdown({
	moduleType,
	htmlName,
	value,
	onChange,
	onBlur,
}: SelectedModuleDropdownProps) {
	const allProducts = useAllModuleProducts(moduleType)
	const choices = useComputed(() => {
		const choices: DropdownChoice[] = []

		// If the current value is missing, add it with some formatting
		const hasCurrent = allProducts.find((p) => p.moduleId === value)
		if (!hasCurrent) choices.push({ id: value, label: `Unknown module: ${value}` })

		// Push all the products. Use an object as an easy way to deduplicate by id
		const choicesObj: Record<string, DropdownChoice> = {}
		for (const product of allProducts) {
			choicesObj[product.moduleId] = { id: product.moduleId, label: product.name }
		}

		return Object.values(choicesObj).sort((a, b) => a.label.localeCompare(b.label))
	}, [allProducts, value])

	return (
		<DropdownInputField
			htmlName={htmlName}
			choices={choices}
			value={value}
			setValue={(value) => onChange(String(value))}
			onBlur={onBlur}
		/>
	)
})

interface SelectedVersionDropdownProps {
	moduleType: ModuleInstanceType
	moduleId: string
	htmlName: string
	value: string | null
	onChange: (value: string | null) => void
	onBlur: () => void
}
const SelectedVersionDropdown = observer(function SelectedVersionDropdown({
	moduleType,
	moduleId,
	htmlName,
	value,
	onChange,
	onBlur,
}: SelectedVersionDropdownProps) {
	const { modules } = useContext(RootAppStoreContext)

	const moduleInfo = modules.getModuleInfo(moduleType, moduleId)
	const { choices: moduleVersionChoices, loaded: choicesLoaded } = useModuleVersionSelectOptions(
		moduleType,
		moduleId,
		moduleInfo,
		true
	)

	return (
		<SimpleDropdownInputField
			id={htmlName}
			value={value as string}
			setValue={(value) => onChange(value as string)}
			onBlur={onBlur}
			noOptionsMessage={choicesLoaded ? 'No compatible versions found' : 'Loading...'}
			badOptionPrefix="Unknown version"
			choices={moduleVersionChoices}
		/>
	)
})
